import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Button } from 'shared-ui';

interface RootErrorBoundaryProps {
    children: ReactNode;
}

interface RootErrorBoundaryState {
    hasError: boolean;
}

/**
 * Catches render/lifecycle errors anywhere below the router so a crash shows a
 * recoverable fallback instead of a blank screen, and — crucially for staging —
 * logs the underlying error with its component stack. Pair with source maps
 * (vite build.sourcemap) to decode minified production stacks.
 */
export class RootErrorBoundary extends Component<RootErrorBoundaryProps, RootErrorBoundaryState> {
    state: RootErrorBoundaryState = { hasError: false };

    static getDerivedStateFromError(): RootErrorBoundaryState {
        return { hasError: true };
    }

    componentDidCatch(error: Error, info: ErrorInfo) {
        console.error('[RootErrorBoundary]', error.message, '\n', error.stack ?? '', '\n', info.componentStack ?? '');
    }

    render() {
        if (!this.state.hasError) {
            return this.props.children;
        }
        return (
            <div className="bg-background flex min-h-screen items-center justify-center p-6">
                <div className="max-w-md space-y-6 text-center">
                    <div className="space-y-2">
                        <h1 className="text-2xl font-extrabold tracking-wide uppercase">Something Went Wrong</h1>
                        <p className="text-muted-foreground text-sm">
                            The app hit an unexpected error. Reloading usually resolves it.
                        </p>
                    </div>
                    <Button
                        onClick={() => {
                            window.location.reload();
                        }}
                        className="w-full"
                    >
                        Reload
                    </Button>
                </div>
            </div>
        );
    }
}
